// Socket service type definitions
import { z } from 'zod';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

// WebSocket message actions
export type SocketAction =
  | 'ping'
  | 'echo'
  | 'lab-partner/register'
  | 'lab-partner/ask'
  | 'subscribe/lab';

// Base message structure sent to WebSocket
export interface SocketMessage {
  action: SocketAction;
  message: LabSubscriptionMessage | LabPartnerRegisterMessage | LabPartnerAskMessage | string;
}

// Lab subscription message
export interface LabSubscriptionMessage {
  labId: string;
}

// Lab subscription response
export interface LabSubscriptionResponse {
  subscribed: string[]; // Array of model IDs
}

// Lab event types
export type LabEventType =
  | 'ModelOperationProgress'
  | 'ModelOperationResult'
  | 'ModelCreated'
  | 'ModelDeleted'
  | 'ModelPublished'
  | 'ModelVisibilityChange'
  | 'ModelVersionCreated'
  | 'ModelVersionDeleted'
  | 'CheckpointCreated';

// Base event structure
export interface LabEvent<T = unknown> {
  type: LabEventType;
  timestamp: string;
  data: T;
}

// ModelOperationProgress event data
export interface ModelOperationProgressData {
  modelId: string;
  versionId: string;
  checkpointId: string;
  stage: string;
  remaining: string;
  percentComplete: string;
}

// ModelOperationResult event data
export interface ModelOperationResultData {
  modelId: string;
  versionId: string;
  checkpointId: string;
  status: string;
  features?: unknown[];
  results?: unknown;
}

// ModelPublished event data
export interface ModelPublishedData {
  modelId: string;
  versionId: string;
  checkpointId: string;
  visibility: string;
}

// ModelVisibilityChange event data
export interface ModelVisibilityChangeData {
  modelId: string;
  visibility: string;
}

// Typed lab events
export type ModelOperationProgressEvent = LabEvent<ModelOperationProgressData>;
export type ModelOperationResultEvent = LabEvent<ModelOperationResultData>;
export type ModelPublishedEvent = LabEvent<ModelPublishedData>;
export type ModelVisibilityChangeEvent = LabEvent<ModelVisibilityChangeData>;

// Union of all typed events
export type TypedLabEvent =
  | ModelOperationProgressEvent
  | ModelOperationResultEvent
  | ModelPublishedEvent
  | ModelVisibilityChangeEvent;

// Eject message (server-to-client when user is disabled/banned)
export interface EjectMessage {
  action: 'eject';
  data: { status: string };
}

// Socket event for internal communication
export interface SocketEvent {
  type: 'connection_status' | 'subscription_confirmed' | 'lab_event' | 'error' | 'lab_partner_stream' | 'eject';
  data?: ConnectionStatus | LabSubscriptionResponse | TypedLabEvent | LabPartnerStreamMessage | EjectMessage['data'] | { message: string } | Record<string, unknown>;
}

// Event history item for debugging
export interface EventHistoryItem {
  id: string;
  type: LabEventType;
  timestamp: string;
  modelId?: string;
  rawData: object;
}

// ============================================
// Lab Partner Types
// ============================================

// Lab Partner register message
export interface LabPartnerRegisterMessage {
  sessionId: string;
}

// Lab Partner ask message
export interface LabPartnerAskMessage {
  sessionId: string;
  prompt: string;
  requestId: string;
  domainId: string;
  outcomeId: string;
  modelId: string;
  versionId: string;
  checkpointId?: string;
  results?: { statistics: unknown[]; messages: unknown[]; errors?: unknown[] };
  timestamp?: string;
  features: Array<{
    featureId?: string;
    name: string;
    description: string;
    results?: { statistics: unknown[]; messages: unknown[]; errors?: unknown[] };
    formula: string;
  }>;
}

// Lab Partner stream event types
export type LabPartnerStreamEventType =
  | 'text_delta'
  | 'model_action'
  | 'feature_action'
  | 'done'
  | 'error'
  | 'progress';

// LP event count keys for debug panel tracking
// text_delta excluded — too high-frequency (per-token), thrashes store/UI during streams
// feature_action is broken out by action type (add/edit/remove)
export type LpEventCountKey =
  | 'model_action'
  | 'feature_add'
  | 'feature_edit'
  | 'feature_remove'
  | 'progress'
  | 'done'
  | 'error';

// Lab Partner message schemas for validation
const TextDeltaDataSchema = z.object({
  content: z.string(),
  isFinal: z.boolean().optional(),
});

const ModelActionDataSchema = z.object({
  content: z.string(),
  actions: z.array(z.object({
    content: z.string(),
    action: z.string(),
  })).optional(),
});

const FeatureActionDataSchema = z.object({
  content: z.string(),
  actions: z.array(z.object({
    content: z.string(),
    action: z.enum(['add', 'edit', 'remove']),
    feature: z.object({
      featureId: z.string(),
      // default(null): backend omits these fields on remove actions
      name: z.string().nullable().default(null),
      description: z.string().nullable().default(null),
      formula: z.string().nullable().default(null),
    }).optional(),
  })).optional(),
});

const DoneDataSchema = z.object({
  modelActionsContent: z.string().optional(),
  modelActions: z.array(z.object({
    content: z.string().optional(),
    action: z.string(),
  })).optional(),
  featureActionsContent: z.string().optional(),
  featureActions: z.array(z.object({
    content: z.string().optional(),
    action: z.string(),
    feature: z.object({
      featureId: z.string(),
      // default(null): backend omits these fields on remove actions
      name: z.string().nullable().default(null),
      description: z.string().nullable().default(null),
      formula: z.string().nullable().default(null),
    }).optional(),
  })).optional(),
});

const ErrorDataSchema = z.object({
  errorId: z.string().optional(),
  message: z.string(),
  errorType: z.string().optional(),
  retryable: z.boolean().optional(),
  code: z.string().optional(),
});

const ProgressDataSchema = z.object({
  stepId: z.string(),
  stepType: z.string().optional(),
  label: z.string(),
  status: z.enum(['in_progress', 'completed', 'failed']),
  order: z.number(),
  percentage: z.number().optional(),
  details: z.any().optional(),
  error: z.string().optional(),
});

// Lab Partner stream message schema
export const LabPartnerStreamMessageSchema = z.discriminatedUnion('type', [
  z.object({
    userId: z.string(),
    sessionId: z.string(),
    requestId: z.string(),
    sequence: z.number().optional(),
    timestamp: z.string(),
    type: z.literal('text_delta'),
    data: TextDeltaDataSchema,
    meta: z.object({}).optional(),
  }),
  z.object({
    userId: z.string(),
    sessionId: z.string(),
    requestId: z.string(),
    sequence: z.number().optional(),
    timestamp: z.string(),
    type: z.literal('model_action'),
    data: ModelActionDataSchema,
    meta: z.object({}).optional(),
  }),
  z.object({
    userId: z.string(),
    sessionId: z.string(),
    requestId: z.string(),
    sequence: z.number().optional(),
    timestamp: z.string(),
    type: z.literal('feature_action'),
    data: FeatureActionDataSchema,
    meta: z.object({}).optional(),
  }),
  z.object({
    userId: z.string(),
    sessionId: z.string(),
    requestId: z.string(),
    sequence: z.number().optional(),
    timestamp: z.string(),
    type: z.literal('done'),
    data: DoneDataSchema,
    meta: z.object({}).optional(),
  }),
  z.object({
    userId: z.string(),
    sessionId: z.string(),
    requestId: z.string(),
    sequence: z.number().optional(),
    timestamp: z.string(),
    type: z.literal('error'),
    data: ErrorDataSchema,
    meta: z.object({}).optional(),
  }),
  z.object({
    userId: z.string(),
    sessionId: z.string(),
    requestId: z.string(),
    sequence: z.number().optional(),
    timestamp: z.string(),
    type: z.literal('progress'),
    data: ProgressDataSchema,
  }),
]);

export type LabPartnerStreamMessage = z.infer<typeof LabPartnerStreamMessageSchema>;

// Lab Partner stream state
export interface LabPartnerStreamState {
  requestId: string;
  sessionId: string;
  accumulatedText: string;
  startTime: number;
  lastUpdate: number;
  isComplete: boolean;
  currentProgressLabel?: string;
  featureActions?: Array<{ content?: string; action: string; feature?: { featureId: string; name: string | null; description: string | null; formula: string | null } }>;
  modelActions?: Array<{ content?: string; action: string }>;
  // Sequence tracking for ordered message processing
  nextExpectedSequence?: number;
  messageBuffer?: Map<number, LabPartnerStreamMessage>; // sequence -> message
  lastProcessedSequence?: number;
  bufferTimeout?: NodeJS.Timeout; // timeout for waiting on missing sequences
  pendingFrame: number | null; // rAF handle for throttled stream updates
}